import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';
import RichText from '../components/RichText';
import InvitesSection from '../components/InvitesSection';

// The reader feed (design/016): notifications across every scope the user
// belongs to — courses they've engaged with and groups they're in. Opening
// the page marks the whole feed read so the sidebar badge clears.
//
// Also renders the actionable Invites section at the top (design/021 UX
// follow-up), so a fresh invite is visible + resolvable from the same place
// the badge points to.
export default function NotificationsPage() {
  const { loading, error, data, reload } = useAsync(async () => {
    const [feed, groupInvites, courseInvites, practiceInvites, eventInvites, boards, replies] = await Promise.all([
      api.notificationsFeed().catch(() => []),
      api.listMyInvites().catch(() => []),
      api.listMyCourseInvites().catch(() => []),
      api.listMyPracticeInvites().catch(() => []),
      api.listMyEventInvites().catch(() => []),
      api.notificationsBoards().catch(() => []),
      api.notificationsReplies().catch(() => []),
    ]);
    return {
      feed: Array.isArray(feed) ? feed : [],
      groupInvites: Array.isArray(groupInvites) ? groupInvites : [],
      courseInvites: Array.isArray(courseInvites) ? courseInvites : [],
      practiceInvites: Array.isArray(practiceInvites) ? practiceInvites : [],
      eventInvites: Array.isArray(eventInvites) ? eventInvites : [],
      boards: Array.isArray(boards) ? boards : [],
      replies: Array.isArray(replies) ? replies : [],
    };
  }, []);

  useEffect(() => {
    // Pending invites stop counting in the bell once seen here (V62).
    api.notificationsInvitesSeen()
      .then(() => window.dispatchEvent(new Event('badge-refresh')))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Fire-and-forget: clear unread once the list has loaded.
    if (!loading && Array.isArray(data?.feed) && data.feed.some((n) => !n.read)) {
      api.notificationsMarkAllRead()
        .then(() => window.dispatchEvent(new Event('badge-refresh')))
        .catch(() => {});
    }
  }, [loading, data]);

  const feed = data?.feed || [];

  return (
    <div className="page">
      <PageHead icon="🔔" title="Notifications" sub="Invites, replies from your instructors, new conversation posts, and announcements" />
      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <>
          <InvitesSection
            groupInvites={data.groupInvites}
            courseInvites={data.courseInvites}
            practiceInvites={data.practiceInvites}
            eventInvites={data.eventInvites}
            onChanged={reload}
          />

          {data.replies.length > 0 && (
            <Section title="Replies from your instructors">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.replies.map((r) => <ReplyRow key={r.submissionId} r={r} />)}
              </div>
            </Section>
          )}

          {data.boards.length > 0 && (
            <Section title="New in conversations">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.boards.map((b) => <BoardRow key={`${b.scopeType}-${b.scopeId}`} b={b} />)}
              </div>
            </Section>
          )}

          {feed.length === 0
            && data.boards.length === 0
            && data.replies.length === 0
            && data.groupInvites.length === 0
            && data.courseInvites.length === 0
            && data.practiceInvites.length === 0
            && data.eventInvites.length === 0 ? (
            <Section><Empty>No new notifications</Empty></Section>
          ) : feed.length === 0 ? null : (
            <Announcements feed={feed} />
          )}
        </>
      )}
    </div>
  );
}

// An instructor replied to your reflection / question. Opening the lesson or
// practice marks it seen, which clears it from here and the badge.
function ReplyRow({ r }) {
  const q = r.kind === 'QUESTION';
  const when = r.at ? new Date(r.at).toLocaleString() : '';
  return (
    <Link to={r.openPath}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', textDecoration: 'none',
        border: '1px solid var(--border)',
        borderRadius: 10, color: 'inherit', background: '#f5f7ff',
      }}>
      <span style={{ fontSize: '1.1rem' }}>🎓</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>
          <b>{r.instructorName}</b> replied to your {q ? 'question' : 'reflection'}
          {r.count > 1 ? ` (${r.count} new)` : ''}
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>
          {r.program?.kind === 'practice' ? '🧘' : '📚'} {r.program?.title}{r.lessonTitle ? ` · ${r.lessonTitle}` : ''}{when ? ` · ${when}` : ''}
        </div>
        {r.excerpt && (
          <div style={{ fontSize: '0.85rem', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            “{r.excerpt}”
          </div>
        )}
      </div>
      <span style={{ color: 'var(--muted-2)' }}>›</span>
    </Link>
  );
}

// A conversation (Event / Group board) with posts you haven't seen. Opening
// it marks the conversation read, which clears it from here and the badge.
function BoardRow({ b }) {
  const isEvent = b.scopeType === 'EVENT';
  const when = b.lastPostAt ? new Date(b.lastPostAt).toLocaleString() : '';
  return (
    <Link to={isEvent ? `/event/${b.scopeId}` : `/group/${b.scopeId}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', textDecoration: 'none',
        border: '1px solid var(--border)',
        borderRadius: 10, color: 'inherit', background: '#f5f7ff',
      }}>
      <span style={{
        fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
        padding: '2px 8px', borderRadius: 999,
        background: isEvent ? '#fff7ed' : '#ecfdf5', color: isEvent ? '#c2410c' : 'var(--green-text, #15803d)',
      }}>{isEvent ? 'Event' : 'Group'}</span>
      <b style={{ flex: 1, minWidth: 0 }}>{b.name || (isEvent ? 'Event' : 'Group')}</b>
      <span style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>
        {b.newPosts} new post{b.newPosts === 1 ? '' : 's'}{when ? ` · ${when}` : ''}
      </span>
      <span style={{ color: 'var(--muted-2)' }}>›</span>
    </Link>
  );
}

// Leader announcements (design/016). They stay after being read; each one
// can be deleted, which hides it for this person only.
function Announcements({ feed }) {
  const [hidden, setHidden] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const shown = feed.filter((n) => !hidden.has(n.id));
  if (shown.length === 0) return null;
  const remove = async (n) => {
    if (!window.confirm('Delete this announcement? It will be removed from your notifications.')) return;
    setBusy(n.id);
    try {
      await api.notificationDismiss(n.id);
      setHidden((h) => new Set(h).add(n.id));
      window.dispatchEvent(new Event('badge-refresh'));
    } catch (e) {
      window.alert(e?.message || 'Could not delete the announcement.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <Section title="Announcements">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {shown.map((n) => (
          <NotificationCard key={n.id} n={n} onDelete={() => remove(n)} deleting={busy === n.id} />
        ))}
      </div>
    </Section>
  );
}

function NotificationCard({ n, onDelete, deleting }) {
  const when = n.createdAt ? new Date(n.createdAt).toLocaleString() : '';
  const chipBg = n.scopeType === 'COURSE' ? 'var(--indigo-50, #eef2ff)' : '#ecfdf5';
  const chipFg = n.scopeType === 'COURSE' ? 'var(--indigo-700, #4338ca)' : 'var(--green-text, #15803d)';
  return (
    <div className="card" style={{
      // Unread: pale indigo background (2026-10-15, replaces the left border).
      background: n.read ? 'var(--card, #fff)' : '#f5f7ff', border: '1px solid var(--border, #e5e7eb)',
      borderRadius: 'var(--radius, 12px)', boxShadow: 'var(--shadow-card)', padding: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e',
        }}>📣 Announcement</span>
        <span style={{
          fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          padding: '2px 8px', borderRadius: 999, background: chipBg, color: chipFg,
        }}>{n.scopeType === 'COURSE' ? 'Course' : 'Group'}</span>
        <span style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>{n.scopeName}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {!n.read && <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--indigo-600, #4f46e5)' }}>NEW</span>}
          {onDelete && (
            <button type="button" onClick={onDelete} disabled={deleting}
              title="Delete announcement" aria-label="Delete announcement"
              style={{
                border: '1px solid var(--border, #e5e7eb)', background: 'transparent', borderRadius: 8,
                padding: '2px 10px', fontSize: '0.8125rem', color: 'var(--muted)', cursor: 'pointer',
              }}>{deleting ? 'Deleting…' : 'Delete'}</button>
          )}
        </span>
      </div>
      <div style={{ fontSize: '1.0625rem', fontWeight: 700, marginBottom: 6 }}>{n.title}</div>
      <RichText html={n.body} />
      <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--muted)' }}>
        {n.authorName}{when ? ` · ${when}` : ''}
      </div>
    </div>
  );
}
